import { useEffect, useState } from "react";
import axios from "axios";
import { Lock, LogIn, Package, FileSignature, Building2, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { PaymentBadge } from "@/components/PaymentOptions";
import DispatchForm from "@/components/DispatchForm";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "orders", label: "Order Requests", icon: Package },
  { id: "waivers", label: "Signed Waivers", icon: FileSignature },
  { id: "inquiries", label: "Wholesale Inquiries", icon: Building2 },
];

const fmtDate = (s) => (s ? new Date(s).toLocaleString() : "—");

export default function Admin() {
  const { user, loading, login } = useAuth();
  const [tab, setTab] = useState("orders");
  const [data, setData] = useState({ orders: [], waivers: [], inquiries: [] });
  const [error, setError] = useState("");
  const [fetching, setFetching] = useState(false);
  const [currentVersion, setCurrentVersion] = useState("");
  const VersionBadge = ({ v }) => (
    <span data-testid="agreement-version-badge" className={`inline-block font-tech text-[10px] uppercase tracking-wide px-1.5 py-0.5 border rounded-sm ${
      v === currentVersion ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
      {v || "unversioned"}{v && v !== currentVersion ? " · superseded" : ""}
    </span>
  );

  useEffect(() => {
    if (!user) return;
    setFetching(true);
    setError("");
    Promise.all([
      axios.get(`${API}/admin/orders`, { withCredentials: true }),
      axios.get(`${API}/admin/waivers`, { withCredentials: true }),
      axios.get(`${API}/admin/inquiries`, { withCredentials: true }),
      axios.get(`${API}/agreement`),
    ])
      .then(([o, w, i, a]) => { setData({ orders: o.data, waivers: w.data, inquiries: i.data }); setCurrentVersion(a.data.version); })
      .catch((e) => setError(e.response?.status === 403 ? "forbidden" : "load"))
      .finally(() => setFetching(false));
  }, [user]);

  const [dispatching, setDispatching] = useState(null);
  const advance = async (oid, body = {}) => {
    await axios.patch(`${API}/admin/orders/${oid}/advance`, body, { withCredentials: true });
    const o = await axios.get(`${API}/admin/orders`, { withCredentials: true });
    setData((d) => ({ ...d, orders: o.data }));
    setDispatching(null);
  };
  const nextStepIndex = (o) => o.timeline?.findIndex((s) => !s.done);

  if (loading) {
    return (
      <main className="max-w-6xl mx-auto px-4 py-24 text-center" data-testid="admin-loading">
        <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" />
      </main>
    );
  }

  if (!user) {
    return (
      <main className="max-w-md mx-auto px-4 py-24 text-center" data-testid="admin-signin-prompt">
        <Lock className="h-10 w-10 text-slate-300 mx-auto" />
        <h1 className="font-display text-2xl font-semibold text-slate-900 mt-4">Owner Dashboard</h1>
        <p className="text-sm text-slate-600 mt-2">Sign in with the owner Google account to view order requests, signed waivers, and wholesale inquiries.</p>
        <Button onClick={login} data-testid="admin-signin-btn" className="mt-6 gap-2">
          <LogIn className="h-4 w-4" /> Sign in with Google
        </Button>
      </main>
    );
  }

  if (error === "forbidden") {
    return (
      <main className="max-w-md mx-auto px-4 py-24 text-center" data-testid="admin-access-denied">
        <Lock className="h-10 w-10 text-red-300 mx-auto" />
        <h1 className="font-display text-2xl font-semibold text-slate-900 mt-4">Access Restricted</h1>
        <p className="text-sm text-slate-600 mt-2">This dashboard is restricted to the store owner account.</p>
      </main>
    );
  }

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="admin-page">
      <p className="font-tech text-xs uppercase tracking-[0.18em] text-sky-700 mb-2">Private · Owner Only</p>
      <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Order Dashboard</h1>

      <div className="flex flex-wrap gap-2 mt-6 mb-6">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
            className={`inline-flex items-center gap-2 font-tech text-xs uppercase tracking-wide px-3 py-1.5 border rounded-full transition-colors ${
              tab === t.id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"}`}>
            <t.icon className="h-3.5 w-3.5" /> {t.label} ({data[t.id].length})
          </button>
        ))}
      </div>

      {fetching && <p className="font-tech text-sm text-slate-500 py-8">Loading records...</p>}

      {!fetching && tab === "orders" && (
        <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="admin-orders-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 font-tech text-[11px] uppercase tracking-wide text-slate-500">
                <th className="text-left p-3">Order ID</th><th className="text-left p-3">Date</th>
                <th className="text-left p-3">Customer</th><th className="text-left p-3">Items</th>
                <th className="text-right p-3">Total</th><th className="text-left p-3">Status</th>
                <th className="text-left p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.orders.map((o) => (
                <tr key={o.order_id} className="border-b border-slate-100" data-testid={`admin-order-row-${o.order_id}`}>
                  <td className="p-3 font-tech font-medium text-slate-900">{o.order_id}</td>
                  <td className="p-3 text-slate-600">{fmtDate(o.created_at)}</td>
                  <td className="p-3 text-slate-700">{o.customer?.name}{o.customer?.entity ? ` · ${o.customer.entity}` : ""}<br/>
                    <span className="text-xs text-slate-400">{o.customer?.email}</span></td>
                  <td className="p-3 text-slate-600">{o.items?.map((i) => `${i.name} ×${i.qty}`).join(", ")}</td>
                  <td className="p-3 text-right font-tech font-semibold text-slate-900">${o.total?.toFixed(2)}</td>
                  <td className="p-3 text-xs text-slate-600 max-w-[220px]">{o.status}<br/><span className="inline-flex flex-wrap gap-1 mt-1"><VersionBadge v={o.agreement_version} /><PaymentBadge payment={o.payment} /></span></td>
                  <td className="p-3">
                    {o.timeline?.every((s) => s.done) ? (
                      <span className="font-tech text-[11px] uppercase tracking-wide text-emerald-700">Complete</span>
                    ) : dispatching === o.order_id ? (
                      <DispatchForm orderId={o.order_id} onSubmit={(body) => advance(o.order_id, body)} onCancel={() => setDispatching(null)} />
                    ) : (
                      <button onClick={() => (nextStepIndex(o) === 2 ? setDispatching(o.order_id) : advance(o.order_id).catch(() => {}))} data-testid={`admin-advance-btn-${o.order_id}`}
                        className="font-tech text-[11px] uppercase tracking-wide bg-sky-700 hover:bg-sky-800 text-white px-2.5 py-1 rounded-full transition-colors whitespace-nowrap">
                        {nextStepIndex(o) === 2 ? "Mark dispatched" : "Advance"}
                      </button>
                    )}
                    {o.shipment && (
                      <p className="font-tech text-[10px] text-slate-500 mt-1.5 whitespace-nowrap" data-testid={`admin-shipment-${o.order_id}`}>
                        {o.shipment.carrier} · {o.shipment.tracking_number}
                      </p>
                    )}
                  </td>
                </tr>
              ))}
              {data.orders.length === 0 && <tr><td colSpan="7" className="p-8 text-center text-slate-400">No order requests yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {!fetching && tab === "waivers" && (
        <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="admin-waivers-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 font-tech text-[11px] uppercase tracking-wide text-slate-500">
                <th className="text-left p-3">Signed By</th><th className="text-left p-3">Title</th>
                <th className="text-left p-3">Email</th><th className="text-left p-3">Order</th>
                <th className="text-left p-3">Signed At</th><th className="text-left p-3">Reference / Agreement</th>
              </tr>
            </thead>
            <tbody>
              {data.waivers.map((w) => (
                <tr key={w.waiver_id} className="border-b border-slate-100" data-testid={`admin-waiver-row-${w.waiver_id}`}>
                  <td className="p-3 font-medium text-slate-900">{w.signature_name}</td>
                  <td className="p-3 text-slate-600">{w.signer_title || "—"}</td>
                  <td className="p-3 text-slate-600">{w.email}</td>
                  <td className="p-3 font-tech text-slate-700">{w.order_id}</td>
                  <td className="p-3 text-slate-600">{fmtDate(w.signed_at)}</td>
                  <td className="p-3 font-tech text-xs text-slate-400">{w.waiver_id?.slice(0, 8)}<br/><VersionBadge v={w.version} /></td>
                </tr>
              ))}
              {data.waivers.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-slate-400">No signed waivers yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {!fetching && tab === "inquiries" && (
        <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="admin-inquiries-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 font-tech text-[11px] uppercase tracking-wide text-slate-500">
                <th className="text-left p-3">Name</th><th className="text-left p-3">Entity</th>
                <th className="text-left p-3">Email</th><th className="text-left p-3">Volume</th>
                <th className="text-left p-3">Message</th><th className="text-left p-3">Date</th>
              </tr>
            </thead>
            <tbody>
              {data.inquiries.map((i) => (
                <tr key={i.inquiry_id} className="border-b border-slate-100" data-testid={`admin-inquiry-row-${i.inquiry_id}`}>
                  <td className="p-3 font-medium text-slate-900">{i.name}</td>
                  <td className="p-3 text-slate-600">{i.entity || "—"}</td>
                  <td className="p-3 text-slate-600">{i.email}</td>
                  <td className="p-3 text-slate-600">{i.volume || "—"}</td>
                  <td className="p-3 text-slate-600 max-w-xs truncate">{i.message || "—"}</td>
                  <td className="p-3 text-slate-600">{fmtDate(i.created_at)}</td>
                </tr>
              ))}
              {data.inquiries.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-slate-400">No wholesale inquiries yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
